import { useEffect, useMemo, useState } from 'react';
import { ClipboardCopy, Filter, Hammer, Plus, Trash2 } from 'lucide-react';
import { getOperatorsForColumnType, tablesForSource, type HuntingSource, type KqlTableDef, type KqlColumnDef } from '../../lib/kqlCatalog';

type FilterRow = {
  id: string;
  column: string;
  operator: string;
  value: string;
};

interface KustoForgeBuilderProps {
  source: HuntingSource;
  schemaTables?: KqlTableDef[];
  onApplyQuery: (query: string) => void;
  customerName?: string;
  disabled?: boolean;
}

function uid(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function quoteValue(value: string, type?: string): string {
  const raw = value.trim();
  if (!raw) return '""';
  const lowerType = String(type ?? '').toLowerCase();
  if (lowerType === 'int' || lowerType === 'long' || lowerType === 'real' || lowerType === 'decimal') return raw;
  if (lowerType === 'bool') return /^(true|false)$/i.test(raw) ? raw.toLowerCase() : 'false';
  if (lowerType === 'datetime') {
    if (/^(ago|now|datetime)\s*\(/i.test(raw)) return raw;
    return `datetime(${raw})`;
  }
  if (raw.startsWith('(') || raw.startsWith('dynamic(') || raw.startsWith('parse_json(')) return raw;
  return `"${raw.replace(/"/g, '\\"')}"`;
}

function renderFilter(row: FilterRow, column?: KqlColumnDef): string | null {
  if (!row.column || !row.operator) return null;
  const op = row.operator.trim();
  const value = row.value.trim();
  if (['isnotempty()', 'isempty()', 'isnull()', 'isnotnull()'].includes(op)) {
    return `| where ${op.replace('()', '')}(${row.column})`;
  }
  if (op === 'has_any()') {
    const values = value.split(',').map((part) => part.trim()).filter(Boolean).map((v) => quoteValue(v, 'string')).join(', ');
    return `| where ${row.column} has_any (${values || '"value"'})`;
  }
  if (op === 'array_length()') return `| where array_length(${row.column}) > ${value || '0'}`;
  if (op === 'bag_keys()') return `| extend ${row.column}_keys = bag_keys(${row.column})`;
  if (op === 'between') return `| where ${row.column} between (${value || 'ago(7d) .. now()'})`;
  if (op === 'matches regex') return `| where ${row.column} matches regex ${quoteValue(value || 'pattern', 'string')}`;
  if (op === 'in' || op === '!in' || op === 'in~') {
    const values = value.split(',').map((part) => part.trim()).filter(Boolean).map((v) => quoteValue(v, column?.type)).join(', ');
    return `| where ${row.column} ${op} (${values || '"value"'})`;
  }
  return `| where ${row.column} ${op} ${quoteValue(value || 'value', column?.type)}`;
}

export function KustoForgeBuilder({ source, schemaTables = [], onApplyQuery, customerName, disabled }: KustoForgeBuilderProps) {
  const tables = useMemo(() => tablesForSource(source, schemaTables), [source, schemaTables]);
  const [open, setOpen] = useState(false);
  const [tableName, setTableName] = useState(() => (source === 'defender' ? 'DeviceProcessEvents' : 'SecurityIncident'));
  const [timeRange, setTimeRange] = useState(source === 'defender' ? '7d' : '24h');
  const [filters, setFilters] = useState<FilterRow[]>([{ id: uid(), column: '', operator: '==', value: '' }]);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [sortColumn, setSortColumn] = useState(source === 'defender' ? 'Timestamp' : 'TimeGenerated');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [limit, setLimit] = useState(100);
  const [summarizeBy, setSummarizeBy] = useState('');

  const activeTable = useMemo(() => tables.find((t) => t.name === tableName) ?? tables[0], [tables, tableName]);
  const columns = activeTable?.columns ?? [];

  useEffect(() => {
    if (!tables.length) return;
    if (!tables.some((t) => t.name === tableName)) {
      const preferred = tables.find((t) => t.name === (source === 'defender' ? 'DeviceProcessEvents' : 'SecurityIncident')) ?? tables[0];
      if (preferred) setTableName(preferred.name);
    }
  }, [source, tables, tableName]);
  const timeColumn = columns.some((c) => c.name === 'TimeGenerated') ? 'TimeGenerated' : columns.some((c) => c.name === 'Timestamp') ? 'Timestamp' : '';

  const generatedQuery = useMemo(() => {
    if (!activeTable) return '';
    const lines: string[] = [activeTable.name];
    if (timeColumn && timeRange.trim()) lines.push(`| where ${timeColumn} > ago(${timeRange.trim()})`);
    for (const row of filters) {
      const column = columns.find((c) => c.name === row.column);
      const rendered = renderFilter(row, column);
      if (rendered) lines.push(rendered);
    }
    if (summarizeBy.trim()) {
      lines.push(`| summarize Count=count() by ${summarizeBy.trim()}`);
      lines.push('| order by Count desc');
    } else {
      if (selectedColumns.length > 0) lines.push(`| project ${selectedColumns.join(', ')}`);
      if (sortColumn) lines.push(`| order by ${sortColumn} ${sortDirection}`);
    }
    lines.push(`| take ${Math.min(Math.max(Number(limit) || 100, 1), 5000)}`);
    return lines.join('\n');
  }, [activeTable, columns, filters, limit, selectedColumns, sortColumn, sortDirection, summarizeBy, timeColumn, timeRange]);

  function updateFilter(id: string, patch: Partial<FilterRow>) {
    setFilters((rows) => rows.map((row) => row.id === id ? { ...row, ...patch } : row));
  }

  function addFilter() {
    setFilters((rows) => [...rows, { id: uid(), column: columns[0]?.name ?? '', operator: '==', value: '' }]);
  }

  function removeFilter(id: string) {
    setFilters((rows) => rows.length > 1 ? rows.filter((row) => row.id !== id) : [{ id: uid(), column: '', operator: '==', value: '' }]);
  }

  function changeTable(nextTable: string) {
    const t = tables.find((item) => item.name === nextTable);
    setTableName(nextTable);
    setFilters([{ id: uid(), column: '', operator: '==', value: '' }]);
    setSelectedColumns([]);
    setSortColumn(t?.columns.some((c) => c.name === 'TimeGenerated') ? 'TimeGenerated' : t?.columns.some((c) => c.name === 'Timestamp') ? 'Timestamp' : t?.columns[0]?.name ?? '');
    setSummarizeBy('');
  }

  async function copyQuery() {
    try { await navigator.clipboard.writeText(generatedQuery); } catch { /* clipboard unavailable */ }
  }

  return (
    <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm font-bold text-foreground"><Hammer size={16} className="text-primary" /> KQL Forge Builder</div>
          <p className="mt-1 text-xs text-muted-foreground">
            Form-based query builder inspired by KustoForge patterns: select table, filters, columns, sort and limit, then apply the generated KQL.
            {customerName ? ` Context: ${customerName}.` : ''}
          </p>
        </div>
        <button
          type="button"
          className="rounded-lg border border-border bg-background px-3 py-2 text-xs font-semibold text-foreground hover:bg-muted"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? 'Hide builder' : 'Open builder'}
        </button>
      </div>

      {open && (
        <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(360px,0.8fr)]">
          <div className="space-y-4">
            <div className="grid gap-3 md:grid-cols-3">
              <label className="text-xs font-semibold text-muted-foreground">
                Table
                <select className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground" value={activeTable?.name ?? ''} onChange={(e) => changeTable(e.target.value)} disabled={disabled}>
                  {tables.map((t) => <option key={t.name} value={t.name}>{t.name}</option>)}
                </select>
              </label>
              <label className="text-xs font-semibold text-muted-foreground">
                Time range
                <input className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground" value={timeRange} onChange={(e) => setTimeRange(e.target.value)} placeholder="24h / 7d" disabled={disabled || !timeColumn} />
              </label>
              <label className="text-xs font-semibold text-muted-foreground">
                Limit
                <input className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground" type="number" min={1} max={5000} value={limit} onChange={(e) => setLimit(Number(e.target.value) || 100)} disabled={disabled} />
              </label>
            </div>

            <div className="rounded-xl border border-border bg-muted/20 p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-muted-foreground"><Filter size={13} /> Filters</div>
                <button type="button" onClick={addFilter} className="inline-flex items-center gap-1 rounded-lg border border-border bg-background px-2 py-1 text-xs font-semibold hover:bg-muted"><Plus size={12} /> Add filter</button>
              </div>
              <div className="space-y-2">
                {filters.map((row) => {
                  const column = columns.find((c) => c.name === row.column);
                  const operators = getOperatorsForColumnType(column?.type).slice(0, 18);
                  return (
                    <div key={row.id} className="grid gap-2 md:grid-cols-[minmax(0,1fr)_170px_minmax(0,1fr)_40px]">
                      <select className="rounded-lg border border-border bg-background px-3 py-2 text-xs text-foreground" value={row.column} onChange={(e) => updateFilter(row.id, { column: e.target.value, operator: '==' })}>
                        <option value="">Column...</option>
                        {columns.map((c) => <option key={c.name} value={c.name}>{c.name}{c.type ? ` · ${c.type}` : ''}</option>)}
                      </select>
                      <select className="rounded-lg border border-border bg-background px-3 py-2 text-xs text-foreground" value={row.operator} onChange={(e) => updateFilter(row.id, { operator: e.target.value })}>
                        {operators.map((op) => <option key={op.label} value={op.label}>{op.label} · {op.args}</option>)}
                      </select>
                      <input className="rounded-lg border border-border bg-background px-3 py-2 text-xs text-foreground" value={row.value} onChange={(e) => updateFilter(row.id, { value: e.target.value })} placeholder="value, comma list, ago(7d) .. now()" />
                      <button type="button" onClick={() => removeFilter(row.id)} className="inline-flex items-center justify-center rounded-lg border border-border bg-background text-muted-foreground hover:bg-red-500/10 hover:text-red-600"><Trash2 size={14} /></button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="grid gap-3 lg:grid-cols-2">
              <div className="rounded-xl border border-border bg-muted/20 p-3">
                <div className="mb-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">Project columns</div>
                <div className="max-h-44 space-y-1 overflow-y-auto rounded-lg border border-border bg-background p-2">
                  {columns.map((c) => (
                    <label key={c.name} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 text-xs hover:bg-muted">
                      <input type="checkbox" checked={selectedColumns.includes(c.name)} onChange={(e) => setSelectedColumns((prev) => e.target.checked ? [...prev, c.name] : prev.filter((x) => x !== c.name))} />
                      <span className="font-mono text-foreground">{c.name}</span>
                      {c.type ? <span className="text-muted-foreground">{c.type}</span> : null}
                    </label>
                  ))}
                </div>
              </div>
              <div className="rounded-xl border border-border bg-muted/20 p-3 space-y-3">
                <label className="block text-xs font-semibold text-muted-foreground">
                  Summarize by
                  <select className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-xs text-foreground" value={summarizeBy} onChange={(e) => setSummarizeBy(e.target.value)}>
                    <option value="">No summarize</option>
                    {columns.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                  </select>
                </label>
                <div className="grid grid-cols-[1fr_110px] gap-2">
                  <label className="text-xs font-semibold text-muted-foreground">
                    Sort by
                    <select className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-xs text-foreground" value={sortColumn} onChange={(e) => setSortColumn(e.target.value)} disabled={Boolean(summarizeBy)}>
                      <option value="">No sort</option>
                      {columns.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                    </select>
                  </label>
                  <label className="text-xs font-semibold text-muted-foreground">
                    Direction
                    <select className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-xs text-foreground" value={sortDirection} onChange={(e) => setSortDirection(e.target.value as 'asc' | 'desc')} disabled={Boolean(summarizeBy)}>
                      <option value="desc">desc</option>
                      <option value="asc">asc</option>
                    </select>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-border bg-muted/20 p-3">
            <div className="mb-2 flex items-center justify-between gap-2">
              <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Live KQL preview</div>
              <div className="flex gap-2">
                <button type="button" onClick={copyQuery} className="inline-flex items-center gap-1 rounded-lg border border-border bg-background px-2 py-1 text-xs font-semibold hover:bg-muted"><ClipboardCopy size={12} /> Copy</button>
                <button type="button" onClick={() => onApplyQuery(generatedQuery)} className="rounded-lg bg-primary px-2 py-1 text-xs font-semibold text-primary-foreground hover:opacity-90">Apply to editor</button>
              </div>
            </div>
            <pre className="max-h-[28rem] overflow-auto whitespace-pre-wrap rounded-lg border border-border bg-background p-3 font-mono text-xs text-foreground">{generatedQuery}</pre>
          </div>
        </div>
      )}
    </div>
  );
}
